import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HeartHandshake, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const empty = { full_name: "", mobile: "", email: "", address: "", skills: "", interests: "", availability: "" };

export default function VolunteerPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const { t } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post("/public/volunteers", { ...form, status: "Pending" }); setDone(data); setForm(empty); toast.success(t("Registered!")); }
    catch (err) { toast.error(err.response ? (formatApiError(err.response.data?.detail) || t("Submission failed")) : t("Could not send. Please check your connection and try again.")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="public-volunteer-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Give Your Time")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Become a Volunteer")}</h1>
          <p className="text-white/70 mt-3 max-w-xl">{t("Offer your skills and time to yoga, Gau Seva, Annadan, welfare and more. Every hand makes a difference.")}</p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {done ? (
          <div className="bg-white rounded-2xl border border-[#E2DAD0] p-8 text-center" data-testid="volunteer-success">
            <div className="inline-flex h-16 w-16 rounded-full bg-[#E8F0EA] items-center justify-center mb-4"><CheckCircle2 className="h-8 w-8 text-[#194324]" /></div>
            <h3 className="font-display text-2xl font-semibold text-[#194324]">{t("Thank you")}, {done.full_name}!</h3>
            <p className="text-[#5C7264] mt-2">{t("Your volunteer registration is pending review.")}</p>
            <div className="mt-5 bg-[#F9F3E3] rounded-xl p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-[#B38728]">{t("Your Volunteer ID")}</div>
              <div className="text-2xl font-bold text-[#194324] font-mono mt-1">{done.volunteer_id}</div>
            </div>
            <Button onClick={() => setDone(null)} variant="outline" className="mt-6 rounded-full">{t("Register another")}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E2DAD0] p-8 space-y-4">
            <div className="flex items-center gap-2 text-[#194324]"><HeartHandshake className="h-5 w-5 text-[#C59B27]" /><h3 className="font-display text-2xl font-semibold">{t("Volunteer Registration")}</h3></div>
            <div className="space-y-2"><Label>{t("Full Name")}</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="volunteer-form-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("Mobile")}</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="volunteer-form-mobile" /></div>
              <div className="space-y-2"><Label>{t("Email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="volunteer-form-email" /></div>
            </div>
            <div className="space-y-2"><Label>{t("Address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="volunteer-form-address" /></div>
            <div className="space-y-2"><Label>{t("Skills")}</Label><Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder={t("e.g. Teaching, Cooking, Medical")} data-testid="volunteer-form-skills" /></div>
            <div className="space-y-2"><Label>{t("Areas of Interest")}</Label><Input value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} placeholder={t("e.g. Gau Seva, Annadan, Yoga")} data-testid="volunteer-form-interests" /></div>
            <div className="space-y-2"><Label>{t("Availability")}</Label><Textarea value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} rows={2} placeholder={t("e.g. Weekends, mornings")} data-testid="volunteer-form-availability" /></div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#194324] hover:bg-[#2D623A] text-white font-semibold" data-testid="volunteer-form-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Register as Volunteer")}
            </Button>
          </form>
        )}
      </section>
    </div>
  );
}
